import React, { useEffect, useState } from "react"
import dayjs from "dayjs"
import { Button, Grid, Paper } from "@mui/material"
import { DataGrid } from '@mui/x-data-grid'
import LayoutPage from "src/components/page-layout/layout-page"
import DatePickerSelect from "src/components/date/date-picker-select"
import PriceService from "src/services/price-service"
import { DATE_FORMAT } from "src/utils/constants"
import { formatStringToFloatDecimalCase } from "src/utils/format-decimal"
import { formatDate } from "src/utils/format-date"
import { localeBRDataGridText } from "../../utils/data-grid-local-text"

const LIMIT_DEFAULT = 50

const BondPriceTablePage = () => {
  const priceService = new PriceService()
  const [loading, setLoading] = useState(false)
  const [priceList, setPriceList] = useState([])
  const [selectedDate, setSelectedDate] = useState("")

  useEffect(() => {
    priceService.loadBondsLastDate()
      .then(lastDate => {
        if (lastDate) setSelectedDate(dayjs(lastDate).format(DATE_FORMAT))
      })
      .catch(err => console.error("Erro ao carregar data mais recente:", err))
  }, [])

  useEffect(() => {
    if (selectedDate) loadPrices(0)
  }, [selectedDate])

  const loadPrices = (page = 0) => {
    if (!selectedDate) return
    setLoading(true)
    priceService
      .getBondsPrices(selectedDate, LIMIT_DEFAULT, page * LIMIT_DEFAULT)
      .then(data => {
        const rows = data.map((obj, idx) => ({ ...obj, id: idx + page * LIMIT_DEFAULT }))
        setPriceList(rows)
      })
      .catch(err => console.error("Erro ao carregar preços:", err))
      .finally(() => setLoading(false))
  }

  const exportFile = () => priceService.exportBondsDataToCsv(selectedDate)

  const columns = [
    { field: 'Data Referência', headerName: 'Data Referência', flex: 1, minWidth: 150, valueGetter: p => p.row['Data Referência'] ? formatDate(p.row['Data Referência']) : '' },
    { field: 'ISIN', headerName: 'ISIN', flex: 1, minWidth: 150 },
    { field: 'Emissão', headerName: 'Emissão', flex: 1, minWidth: 150, valueGetter: p => p.row['Emissão'] ? formatDate(p.row['Emissão']) : '' },
    { field: 'Vencimento', headerName: 'Vencimento', flex: 1, minWidth: 150, valueGetter: p => p.row['Vencimento'] ? formatDate(p.row['Vencimento']) : '' },
    { field: 'Devedor', headerName: 'Devedor', flex: 1, minWidth: 150, valueGetter: p => p.row['Devedor'] || '' },
    { field: 'Moeda', headerName: 'Moeda', flex: 1, minWidth: 150, valueGetter: p => p.row['Moeda'] || '' },
    { field: 'Valor Nominal', headerName: 'Valor Nominal', flex: 1, minWidth: 150, valueGetter: p => p.row['Valor Nominal'] != null ? formatStringToFloatDecimalCase(p.row['Valor Nominal']) : '' },
    { field: 'Taxa MTM', headerName: 'Taxa MTM', flex: 1, minWidth: 150, valueGetter: p => p.row['Taxa MTM'] != null ? formatStringToFloatDecimalCase(p.row['Taxa MTM']) : '' },
    { field: 'PU MTM Limpo', headerName: 'PU MTM Limpo', flex: 1, minWidth: 150, valueGetter: p => p.row['PU MTM Limpo'] != null ? formatStringToFloatDecimalCase(p.row['PU MTM Limpo']) : '' },
    { field: 'PU MTM Sujo', headerName: 'PU MTM Sujo', flex: 1, minWidth: 150, valueGetter: p => p.row['PU MTM Sujo'] != null ? formatStringToFloatDecimalCase(p.row['PU MTM Sujo']) : '' },
    { field: 'PercPUPAR Limpo', headerName: '%PUPAR Limpo', flex: 1, minWidth: 150, valueGetter: p => p.row['PercPUPAR Limpo'] != null ? formatStringToFloatDecimalCase(p.row['PercPUPAR Limpo']) : '' },
    { field: 'PercPUPAR Sujo', headerName: '%PUPAR Sujo', flex: 1, minWidth: 150, valueGetter: p => p.row['PercPUPAR Sujo'] != null ? formatStringToFloatDecimalCase(p.row['PercPUPAR Sujo']) : '' },
    { field: 'Duration DC', headerName: 'Duration DC', flex: 1, minWidth: 150, valueGetter: p => p.row['Duration DC'] != null ? formatStringToFloatDecimalCase(p.row['Duration DC']) : '' },
    { field: 'Duration Ano', headerName: 'Duration Ano', flex: 1, minWidth: 150, valueGetter: p => p.row['Duration Ano'] != null ? formatStringToFloatDecimalCase(p.row['Duration Ano']) : '' },
    { field: 'Data Próximo Cupom', headerName: 'Data Próximo Cupom', flex: 1, minWidth: 150, valueGetter: p => p.row['Data Próximo Cupom'] ? formatDate(p.row['Data Próximo Cupom']) : '' },
    { field: 'Cupom Accruado', headerName: 'Cupom Accruado', flex: 1, minWidth: 150, valueGetter: p => p.row['Cupom Accruado'] != null ? formatStringToFloatDecimalCase(p.row['Cupom Accruado']) : '' },
  ]

  return (
    <LayoutPage loading={loading} title="Tabela de PU Bonds">
      {/* Header: date picker + export aligned right */}
      <Grid sx={{ mr: { md: 13, xs: 0 } }}>
        <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
          <Grid item>
            <DatePickerSelect
              value={selectedDate ? dayjs(selectedDate) : null}
              onChange={date => date && setSelectedDate(date.format(DATE_FORMAT))}
              label="Data de Exibição"
            />
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={exportFile}>
              Exportar para CSV
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <br />

      <Paper sx={{ height: 600, width: '100%', overflowX: 'auto' }}>
        <DataGrid
          rows={priceList}
          columns={columns}
          loading={loading}
          pagination
          pageSize={LIMIT_DEFAULT}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={({ page }) => loadPrices(page)}
          localeText={localeBRDataGridText}
          disableSelectionOnClick
        />
      </Paper>
    </LayoutPage>
  )
}

export default BondPriceTablePage